// client/src/pages/StatutPage.js
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Edit, Trash2 } from 'lucide-react';
import api from '../utils/api';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";

const StatutPage = () => {
  const [statuts, setStatuts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([{ id: "Titre", desc: false }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ Titre: "" });
  const [editingId, setEditingId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchStatuts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/statut");
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setStatuts(res.data.data);
      } else {
        console.error("Format de réponse inattendu:", res.data);
        setStatuts([]);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des statuts:", err);
      setStatuts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuts();
  }, []);

  const handleOpenDialog = () => {
    setFormData({ Titre: "" });
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await api.put(`/api/statut/${editingId}`, formData);
      } else {
        await api.post("/api/statut", formData);
      }
      fetchStatuts();
      setOpenDialog(false);
      setEditingId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (statut) => {
    setFormData({ Titre: statut.Titre });
    setEditingId(statut.id);
    setOpenDialog(true);
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await api.delete(`/api/statut/${deleteId}`);
      fetchStatuts();
      setOpenDeleteDialog(false);
      setDeleteId(null);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error?.message || "Erreur lors de la suppression du statut");
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.accessor("Titre", {
        header: "Titre",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <div style={{ display: "flex", gap: "4px" }}>
            <IconButton
              color="primary"
              onClick={() => handleEdit(info.row.original)}
            >
              <Edit size={20} />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => handleOpenDeleteDialog(info.row.original.id)}
            >
              <Trash2 size={20} />
            </IconButton>
          </div>
        ),
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    data: statuts,
    columns,
    state: { globalFilter, pagination, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const pageCount = table.getPageCount() || 0;

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Statuts
      </Typography>
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <Button variant="contained" color="primary" onClick={handleOpenDialog}>
          Ajouter un Statut
        </Button>
        <TextField
          label="Recherche globale"
          variant="outlined"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>
      {loading ? (
        <CircularProgress />
      ) : statuts.length === 0 ? (
        <Typography>Aucun statut trouvé.</Typography>
      ) : (
        <>
          <TableContainer component={Paper}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #ccc",
                          textAlign: "left",
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: Math.max(prev.pageIndex - 1, 0),
                }))
              }
              disabled={!table.getCanPreviousPage()}
            >
              Précédent
            </Button>
            <span>
              Page {pagination.pageIndex + 1} sur {pageCount}
            </span>
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: prev.pageIndex + 1,
                }))
              }
              disabled={!table.getCanNextPage()}
            >
              Suivant
            </Button>
          </div>
        </>
      )}

      {/* Dialog pour ajouter/éditer un statut */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingId ? "Éditer un Statut" : "Ajouter un Statut"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Titre"
            fullWidth
            margin="dense"
            value={formData.Titre}
            onChange={(e) =>
              setFormData({ ...formData, Titre: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingId ? "Mettre à jour" : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation pour la suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>Voulez-vous vraiment supprimer ce statut ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Non</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Oui
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StatutPage;
